import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  createCommunityCommentThunk,
  deleteCommunityCommentThunk,
  deleteCommunityPostThunk,
  fetchCommunityCommentsThunk,
  fetchCommunityFeedThunk,
  fetchCommunityTopicsThunk,
  followCommunityAuthorThunk,
  likeCommunityCommentThunk,
  likeCommunityPostThunk,
  resetFeed,
  setFeedMode,
  toggleSavePost,
  unfollowCommunityAuthorThunk,
  unlikeCommunityCommentThunk,
  unlikeCommunityPostThunk,
} from '../../redux/communitySlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import type { AsyncStatus } from '../../redux/communitySlice';
import type { CommunityPost, CommunityTopic } from '../../types';
import CommunityPostCard from '../../components/CommunityPostCard';
import type { CommunityLayoutOutletContext } from '../layout/CommunityLayout';
import './TimelinePage.css';

interface TimelinePageProps {
  initialMode: 'for_you' | 'following' | 'latest';
}

const FEED_TABS: Array<{ value: 'for_you' | 'following' | 'latest'; label: string }> = [
  { value: 'for_you', label: '为你推荐' },
  { value: 'following', label: '关注' },
  { value: 'latest', label: '最新' },
];

const TimelinePage: React.FC<TimelinePageProps> = ({ initialMode }) => {
  const dispatch = useAppDispatch();
  const {
    topics,
    topicsStatus,
    feedItems,
    feedStatus,
    feedCursor,
    feedHasMore,
    feedError,
    comments,
    feedMode,
    savedPostIds,
  } = useAppSelector((state) => state.community);
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? null;

  const { searchTerm, setSearchInput } = useOutletContext<CommunityLayoutOutletContext>();

  const navigate = useNavigate();
  const { topicId: routeTopicId } = useParams<{ topicId?: string }>();
  const [localTopic, setLocalTopic] = useState<string | null>(null);
  const selectedTopic = routeTopicId ?? localTopic;
  const topicLocked = Boolean(routeTopicId);

  useEffect(() => {
    if (!routeTopicId) {
      return;
    }
    setLocalTopic(null);
    if (searchTerm) {
      setSearchInput('');
    }
  }, [routeTopicId, searchTerm, setSearchInput]);
  const [likePendingMap, setLikePendingMap] = useState<Record<string, boolean>>({});
  const [followPendingMap, setFollowPendingMap] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (topicsStatus === 'idle') {
      dispatch(fetchCommunityTopicsThunk());
    }
  }, [topicsStatus, dispatch]);

  useEffect(() => {
    dispatch(setFeedMode(initialMode));
  }, [dispatch, initialMode]);

  useEffect(() => {
    if (feedMode === 'following' && !userId) {
      return;
    }
    const sortValue: 'latest' | 'trending' = feedMode === 'for_you' ? 'trending' : 'latest';
    dispatch(resetFeed());
    dispatch(
      fetchCommunityFeedThunk({
        append: false,
        topic: selectedTopic ?? undefined,
        search: searchTerm || undefined,
        followed_only: feedMode === 'following' ? true : undefined,
        sort: sortValue,
      }),
    );
  }, [dispatch, feedMode, selectedTopic, searchTerm, userId]);

  const topicsDict = useMemo(() => {
    return topics.reduce<Record<string, CommunityTopic>>((acc, topic) => {
      acc[topic.id] = topic;
      return acc;
    }, {});
  }, [topics]);

  const handleTopicSelect = (topicId: string | null) => {
    if (topicLocked) {
      if (!topicId) {
  navigate('/app/community');
        return;
      }
      if (routeTopicId !== topicId) {
  navigate(`/app/community/topics/${topicId}`);
      }
      return;
    }
    setLocalTopic((prev) => (prev === topicId ? null : topicId));
  };

  const handleFeedTabClick = (mode: 'for_you' | 'following' | 'latest') => {
    if (mode === feedMode) {
      return;
    }

    if (topicLocked && mode !== 'for_you') {
  const targetPath = mode === 'following' ? '/app/community/following' : '/app/community/latest';
  navigate(targetPath);
      return;
    }

    dispatch(setFeedMode(mode));

    if (!topicLocked) {
      setLocalTopic(null);
    }

    setSearchInput('');
  };

  const handleLoadMore = () => {
    if (!feedHasMore || !feedCursor) {
      return;
    }
    dispatch(
      fetchCommunityFeedThunk({
        append: true,
        cursor: feedCursor,
        topic: selectedTopic ?? undefined,
        search: searchTerm || undefined,
        followed_only: feedMode === 'following' ? true : undefined,
        sort: feedMode === 'for_you' ? 'trending' : 'latest',
      }),
    );
  };

  const handleLoadComments = (postId: string) => {
    const entry = comments[postId];
    if (!entry || entry.status === 'idle' || entry.status === 'failed') {
      dispatch(fetchCommunityCommentsThunk({ postId }));
    }
  };

  const handleSubmitComment = async (postId: string, content: string, parentCommentId?: string | null) => {
    await dispatch(
      createCommunityCommentThunk({
        postId,
        payload: {
          content,
          parent_comment_id: parentCommentId ?? undefined,
        },
      }),
    ).unwrap();
  };

  const handleDeleteComment = async (postId: string, commentId: string) => {
    await dispatch(deleteCommunityCommentThunk({ postId, commentId })).unwrap();
  };

  const handleToggleCommentLike = async (postId: string, commentId: string, hasLiked: boolean) => {
    const thunk = hasLiked ? unlikeCommunityCommentThunk : likeCommunityCommentThunk;
    await dispatch(thunk({ postId, commentId })).unwrap();
  };

  const handleDeletePost = async (postId: string) => {
    await dispatch(deleteCommunityPostThunk(postId)).unwrap();
  };

  const handleToggleLike = async (postId: string, hasLiked: boolean) => {
    if (likePendingMap[postId]) {
      return;
    }
    setLikePendingMap((prev) => ({ ...prev, [postId]: true }));
    try {
      if (hasLiked) {
        await dispatch(unlikeCommunityPostThunk(postId)).unwrap();
      } else {
        await dispatch(likeCommunityPostThunk(postId)).unwrap();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '操作失败，请稍后再试';
      window.alert(message);
    } finally {
      setLikePendingMap((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    }
  };

  const handleToggleFollow = async (authorId: number, followsAuthor: boolean) => {
    if (followPendingMap[authorId]) {
      return;
    }
    setFollowPendingMap((prev) => ({ ...prev, [authorId]: true }));
    try {
      if (followsAuthor) {
        await dispatch(unfollowCommunityAuthorThunk(authorId)).unwrap();
      } else {
        await dispatch(followCommunityAuthorThunk(authorId)).unwrap();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '操作失败，请稍后再试';
      window.alert(message);
    } finally {
      setFollowPendingMap((prev) => {
        const next = { ...prev };
        delete next[authorId];
        return next;
      });
    }
  };

  const renderFeedStatus = () => {
    if (feedStatus === 'loading' && feedItems.length === 0) {
      return <div className="community-timeline__placeholder">内容加载中...</div>;
    }
    if (feedStatus === 'failed' && feedItems.length === 0) {
      return <div className="community-timeline__error">{feedError || '加载社区动态失败，请稍后再试'}</div>;
    }
    if (feedItems.length === 0) {
      if (feedMode === 'following') {
        return <div className="community-timeline__placeholder">关注一些作者后，这里会出现他们的最新分享。</div>;
      }
      return <div className="community-timeline__placeholder">还没有内容，发布首篇动态开启讨论。</div>;
    }
    return null;
  };

  const handleToggleSave = (post: CommunityPost) => {
    dispatch(toggleSavePost(post));
  };

  const handleOpenComposer = () => {
  navigate('/app/community/compose');
  };

  const handleOpenPost = (postId: string) => {
  navigate(`/app/community/status/${postId}`);
  };

  const handleOpenAuthor = (authorId: number) => {
  navigate(`/app/community/users/${authorId}`);
  };

  return (
    <div className="community-timeline">
      <section className="community-timeline__hero">
        <div className="community-timeline__hero-copy">
          <h1>探索草本社区，分享你的调养心得</h1>
          <p>
            记录真实的调理体验、体质调养计划与每日观察，与志同道合的朋友一起交流、互助与成长。
          </p>
          <div className="community-timeline__hero-actions">
            <button type="button" onClick={handleOpenComposer}>
              ✍️ 发布心得
            </button>
            <span>或浏览下方精选话题，结识新朋友。</span>
          </div>
        </div>
        <div className="community-timeline__hero-illustration" aria-hidden />
      </section>

      <div className="community-timeline__tabs" role="tablist">
        {FEED_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            className={`community-timeline__tab${feedMode === tab.value ? ' community-timeline__tab--active' : ''}`}
            onClick={() => handleFeedTabClick(tab.value)}
            role="tab"
            aria-selected={feedMode === tab.value}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="community-timeline__filters">
        <button
          type="button"
          className={`community-timeline__topic${selectedTopic === null ? ' community-timeline__topic--active' : ''}`}
          onClick={() => handleTopicSelect(null)}
        >
          全部话题
        </button>
        {topics.map((topic) => (
          <button
            key={topic.id}
            type="button"
            className={`community-timeline__topic${selectedTopic === topic.id ? ' community-timeline__topic--active' : ''}`}
            onClick={() => handleTopicSelect(topic.id)}
          >
            #{topic.label}
          </button>
        ))}
      </div>

      {renderFeedStatus()}

      {feedItems.map((post) => {
        const commentEntry = comments[post.post_id];
        const list = commentEntry?.items ?? [];
        const status: AsyncStatus = commentEntry?.status ?? 'idle';
        const errorMsg = commentEntry?.error ?? null;
        const createStatus: AsyncStatus = commentEntry?.createStatus ?? 'idle';
        const createError = commentEntry?.createError ?? null;
        const commentLikePending = commentEntry?.likePending ?? {};
        const hasSaved = post.viewer?.has_saved ?? savedPostIds.includes(post.post_id);

        return (
          <CommunityPostCard
            key={post.post_id}
            post={post}
            topicsDict={topicsDict}
            comments={list}
            commentsStatus={status}
            commentsError={errorMsg}
            createStatus={createStatus}
            createError={createError}
            onLoadComments={() => handleLoadComments(post.post_id)}
            onSubmitComment={(content, parentId) => handleSubmitComment(post.post_id, content, parentId)}
            onDeleteComment={(commentId) => handleDeleteComment(post.post_id, commentId)}
            onToggleCommentLike={(commentId, hasLiked) => handleToggleCommentLike(post.post_id, commentId, hasLiked)}
            commentLikePending={commentLikePending}
            currentUserId={userId}
            onDeletePost={post.permissions?.can_edit ? () => handleDeletePost(post.post_id) : undefined}
            onToggleLike={() => handleToggleLike(post.post_id, post.viewer?.has_liked ?? false)}
            likePending={likePendingMap[post.post_id]}
            onToggleFollow={post.author?.id ? () => handleToggleFollow(post.author.id, post.viewer?.follows_author ?? false) : undefined}
            followPending={post.author?.id ? followPendingMap[post.author.id] : undefined}
            hasSaved={hasSaved}
            onToggleSave={() => handleToggleSave(post)}
            onOpenPost={() => handleOpenPost(post.post_id)}
            onOpenAuthor={post.author?.id ? () => handleOpenAuthor(post.author.id) : undefined}
          />
        );
      })}

      {feedHasMore && feedCursor && (
        <div className="community-timeline__load-more">
          <button type="button" onClick={handleLoadMore} disabled={feedStatus === 'loading'}>
            {feedStatus === 'loading' ? '加载中...' : '加载更多'}
          </button>
        </div>
      )}
    </div>
  );
};

export default TimelinePage;
